/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { api } from "@/lib/api";

const cities = [
  "Marssac-sur-Tarn", "Albi", "Gaillac", "Castres", "Carmaux", "Graulhet",
  "Lavaur", "Mazamet", "Réalmont", "Saint-Sulpice-la-Pointe", "Autre ville du Tarn", "Hors Tarn",
];

const sectors = [
  "Artisanat / Bâtiment", "Commerce / Boutique", "Restauration", "Santé / Bien-être",
  "Services aux entreprises", "Profession libérale", "Industrie / Production",
  "Association / ONG", "E-commerce", "Autre",
];

const budgets = ["Moins de 1 000 €", "1 000 – 2 500 €", "2 500 – 5 000 €", "5 000 – 10 000 €", "Plus de 10 000 €", "Je ne sais pas encore"];

const goals = [
  "Gagner en visibilité locale",
  "Recevoir plus de demandes",
  "Vendre en ligne",
  "Refondre un site existant",
  "Automatiser des tâches",
  "Autre / À définir",
];

const optionsServices = [
  { id: "seo", label: "SEO local" },
  { id: "maintenance", label: "Maintenance" },
  { id: "chatbot", label: "Chatbot IA" },
  { id: "voice", label: "Agent vocal IA" },
  { id: "booking", label: "Réservation en ligne" },
];

export const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", phone: "", email: "",
    city: "", sector: "", has_website: "", main_goal: "", budget: "",
    description: "", services: [],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((s) => s !== id) : [...f.services, id],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.city) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    if (!consent) {
      toast.error("Merci d'accepter les conditions pour envoyer votre demande.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      // Scroll the contact section into view so the user sees the confirmation
      setTimeout(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler au 06 46 79 25 54.");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "w-full bg-white border border-ink/15 px-4 h-12 text-ink placeholder-ink/40 focus:outline-none focus:border-ink transition-colors";
  const labelCls = "block text-xs font-mono-label text-ink/60 mb-2";

  if (submitted) {
    return (
      <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-paper min-h-[80vh] flex items-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center w-full" data-testid="contact-success">
          <div className="inline-flex h-20 w-20 items-center justify-center bg-ink text-white mb-10 rounded-full">
            <Check size={36} strokeWidth={2.5} />
          </div>
          <p className="font-mono-label text-ink/60 mb-4">
            <span className="rule" />Confirmation
          </p>
          <h2 className="font-display text-5xl md:text-7xl text-ink tracking-tight leading-[1.02]">
            Demande envoyée.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-ink/75 leading-relaxed max-w-2xl mx-auto">
            Merci <span className="font-medium text-ink">{form.first_name}</span> !
            Votre demande a bien été enregistrée.<br />
            Marius vous contacte <span className="font-medium text-ink">sous 24 heures</span> pour convenir
            de votre rendez-vous gratuit.
          </p>

          <div className="mt-12 inline-flex flex-col sm:flex-row items-center gap-3 text-sm">
            <a
              href="tel:+33646792554"
              data-testid="contact-success-phone"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-ink text-white hover:bg-ink-600 transition-colors"
            >
              Une question urgente ? 06 46 79 25 54
            </a>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({
                  first_name: "", last_name: "", company: "", phone: "", email: "",
                  city: "", sector: "", has_website: "", main_goal: "", budget: "",
                  description: "", services: [],
                });
              }}
              data-testid="contact-success-new"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 border border-ink/20 text-ink hover:bg-ink hover:text-white transition-colors"
            >
              Envoyer une nouvelle demande
            </button>
          </div>

          <div className="mt-16 pt-10 border-t border-ink/10 text-sm text-ink/55 max-w-xl mx-auto">
            <p className="font-mono-label text-ink/55 mb-3">Récapitulatif de votre demande</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-left">
              <span className="text-ink/55">Email</span><span className="text-ink/85 break-all">{form.email}</span>
              <span className="text-ink/55">Téléphone</span><span className="text-ink/85">{form.phone}</span>
              <span className="text-ink/55">Ville</span><span className="text-ink/85">{form.city}</span>
              {form.company && (<><span className="text-ink/55">Entreprise</span><span className="text-ink/85">{form.company}</span></>)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-paper">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <p className="font-mono-label text-ink/60"><span className="rule" />Parlons de votre projet</p>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
              Proposition de <em className="italic font-light">rendez-vous gratuit</em> sous 24 heures.
            </h2>
            <p className="mt-8 text-ink/65 leading-relaxed">
              Un formulaire, un humain. Pas de robot, pas de spam.<br />
              <strong className="text-ink">Marius</strong> vous répond personnellement sous 24 h
              pour convenir d'un premier rendez-vous.
            </p>
            <div className="mt-10 space-y-4 text-sm text-ink/75">
              <p><span className="font-mono-label text-ink/55 mr-3">Tél.</span> <a className="link-under" href="tel:+33646792554">06 46 79 25 54</a></p>
              <p><span className="font-mono-label text-ink/55 mr-3">Mail</span> <a className="link-under break-all" href="mailto:contact.agency.webup@gmail.com">contact.agency.webup@gmail.com</a></p>
              <p><span className="font-mono-label text-ink/55 mr-3">Adresse</span> Marssac-sur-Tarn — Tarn (81)</p>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-paper-soft border border-ink/10 p-8 md:p-12 space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Prénom *</label>
                <input data-testid="contact-first-name" required className={fieldCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Nom *</label>
                <input data-testid="contact-last-name" required className={fieldCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Entreprise</label>
                <input data-testid="contact-company" className={fieldCls} value={form.company} onChange={(e) => set("company", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Téléphone *</label>
                <input data-testid="contact-phone" type="tel" required className={fieldCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Email *</label>
                <input data-testid="contact-email" type="email" required className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Ville *</label>
                <select data-testid="contact-city" required className={fieldCls} value={form.city} onChange={(e) => set("city", e.target.value)}>
                  <option value="">Sélectionner</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Secteur d’activité</label>
                <select data-testid="contact-sector" className={fieldCls} value={form.sector} onChange={(e) => set("sector", e.target.value)}>
                  <option value="">Sélectionner</option>
                  {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Avez-vous déjà un site web ?</label>
                <select data-testid="contact-has-website" className={fieldCls} value={form.has_website} onChange={(e) => set("has_website", e.target.value)}>
                  <option value="">Sélectionner</option>
                  <option value="oui">Oui</option>
                  <option value="oui_a_refondre">Oui, à refondre</option>
                  <option value="non">Non, pas encore</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Objectif principal</label>
                <select data-testid="contact-goal" className={fieldCls} value={form.main_goal} onChange={(e) => set("main_goal", e.target.value)}>
                  <option value="">Sélectionner</option>
                  {goals.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Budget estimé</label>
                <select data-testid="contact-budget" className={fieldCls} value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                  <option value="">Sélectionner</option>
                  {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Décrivez votre projet</label>
              <textarea
                data-testid="contact-description"
                rows={4}
                className="w-full bg-white border border-ink/15 px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-ink transition-colors resize-none"
                placeholder="Quelques lignes sur votre activité, vos clients et ce que vous attendez de votre site."
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>

            <div>
              <label className={labelCls}>Souhaitez-vous (plusieurs choix possibles) ?</label>
              <div className="flex flex-wrap gap-2">
                {optionsServices.map((s) => {
                  const active = form.services.includes(s.id);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`contact-service-${s.id}`}
                      onClick={() => toggleService(s.id)}
                      className={`px-4 h-10 text-sm border transition-colors ${
                        active ? "bg-ink text-white border-ink" : "bg-white text-ink/80 border-ink/20 hover:border-ink"
                      }`}
                    >
                      {active && <Check size={14} className="inline mr-2 -mt-0.5" />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-2">
              {/* GDPR mandatory consent */}
              <label
                htmlFor="contact-consent"
                className="mb-6 flex items-start gap-3 cursor-pointer p-4 bg-white border border-ink/15 hover:border-cyan-500 transition-colors"
              >
                <input
                  id="contact-consent"
                  type="checkbox"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="contact-consent"
                  className="mt-1 h-4 w-4 accent-cyan-600 shrink-0"
                  aria-required="true"
                  aria-describedby="contact-consent-desc"
                />
                <span id="contact-consent-desc" className="text-sm text-ink/80 leading-relaxed">
                  J'accepte les{" "}
                  <Link to="/cgu" target="_blank" rel="noopener" className="link-under text-ink font-medium">conditions générales</Link>{" "}
                  et la{" "}
                  <Link to="/politique-confidentialite" target="_blank" rel="noopener" className="link-under text-ink font-medium">politique de confidentialité</Link>. <span className="text-rose-600" aria-hidden="true">*</span>
                </span>
              </label>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading || !consent}
                className="group inline-flex items-center gap-3 h-14 px-8 btn-premium text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Réserver mon rendez-vous gratuit avec WebUP"
              >
                {loading ? "Envoi en cours…" : "Réserver mon rendez-vous"}
                <ArrowUpRight size={18} aria-hidden="true" className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <p className="mt-4 text-xs text-ink/50">
                Vos informations sont traitées conformément à notre <Link to="/politique-confidentialite" className="link-under">Politique de confidentialité</Link>. Aucune donnée transmise à des tiers.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
